/**
 * <collection-filters> - applies filters and sorting on the collection page without a page load.
 *
 * Every change in the filter form (or the sort select, linked with form="…") asks Shopify for this
 * section with the new URL parameters (Section Rendering API) and swaps the parts marked
 * [data-swap]: the result count, active filters, filter options with their counts, the apply
 * button and the product grid. The URL is updated, so the filtered view can be shared or reloaded.
 * On phones the filters open as a drawer ([data-filters-open] / [data-filters-close]).
 */
(function () {
  if (customElements.get('collection-filters')) return;

  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-filters-form]');
      this.panel = this.querySelector('[data-filters-panel]');
      this.openButton = this.querySelector('[data-filters-open]');
      this.form?.classList.add('is-enhanced');

      this.addEventListener('change', (event) => {
        if (!event.target.matches('[data-filter-input]')) return;
        clearTimeout(this.timer);
        this.update();
      });

      // Price fields update after a short pause in typing.
      this.addEventListener('input', (event) => {
        if (event.target.type !== 'number') return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.update(), 600);
      });

      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        clearTimeout(this.timer);
        this.update().then(() => this.close());
      });

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-filters-open]')) return this.open();
        if (event.target.closest('[data-filters-close]')) return this.close();
        const link = event.target.closest('a[data-filter-link]');
        if (link && !event.metaKey && !event.ctrlKey) {
          event.preventDefault();
          this.update(new URL(link.href, window.location.origin).searchParams);
        }
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.panel?.classList.contains('is-open')) this.close();
      });
    }

    open() {
      if (!this.panel) return;
      this.panel.classList.add('is-open');
      document.documentElement.classList.add('filters-open');
      this.openButton?.setAttribute('aria-expanded', 'true');
      this.panel.querySelector('[data-filters-close]')?.focus();
    }

    close() {
      if (!this.panel?.classList.contains('is-open')) return;
      this.panel.classList.remove('is-open');
      document.documentElement.classList.remove('filters-open');
      this.openButton?.setAttribute('aria-expanded', 'false');
      this.openButton?.focus();
    }

    /** Current form state as URL parameters, without empty values. */
    formParams() {
      const params = new URLSearchParams();
      // Collections without filters have no form: only the sort select counts.
      if (!this.form) {
        const sort = this.querySelector('select[name="sort_by"]');
        if (sort) params.set('sort_by', sort.value);
        return params;
      }
      for (const [key, value] of new FormData(this.form)) {
        if (value !== '') params.append(key, value);
      }
      return params;
    }

    async update(params = this.formParams()) {
      const query = params.toString();
      const url = `${window.location.pathname}${query ? `?${query}` : ''}`;
      const focusedId = document.activeElement?.id;
      const openGroups = new Set(
        Array.from(this.querySelectorAll('details[data-filter]'))
          .filter((details) => !details.open)
          .map((details) => details.dataset.filter)
      );

      this.classList.add('is-loading');
      try {
        const separator = query ? '&' : '?';
        const response = await fetch(`${url}${separator}section_id=${this.dataset.sectionId}`);
        if (!response.ok) throw new Error(response.statusText);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = html.querySelector('collection-filters');
        if (!next) throw new Error('No results');

        this.querySelectorAll('[data-swap]').forEach((part) => {
          const replacement = next.querySelector(`[data-swap="${part.dataset.swap}"]`);
          if (replacement) part.innerHTML = replacement.innerHTML;
        });

        // Keep the filter groups the visitor collapsed collapsed, and keep focus where it was.
        this.querySelectorAll('details[data-filter]').forEach((details) => {
          if (openGroups.has(details.dataset.filter)) details.open = false;
        });
        if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });

        window.history.replaceState({}, '', url);
      } catch {
        window.location.href = url;
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }

  customElements.define('collection-filters', CollectionFilters);
})();
